// Мобильная версия (PWA) живёт под /m/ и открывается только на
// телефоне. Здесь — чистые проверки «это телефон?» и «куда отправить телефон,
// открывший обычную версию», без React: их зовёт main.tsx до монтирования.

/** Корень мобильной версии. Со слешем: область PWA (scope манифеста и
 *  service worker) — `/m/`, и адрес без слеша в неё не входит — в
 *  установленном приложении он открылся бы как чужая страница. */
export const MOBILE_BASE = "/m/";

/** Флаг сессии «пользователь сам ушёл в полную версию» — тогда телефон с
 *  `/chats`, `/candidates` и прочих обратно в мобильную не отправляем. */
export const DESKTOP_FLAG = "newats.desktop";

export function isMobilePath(pathname: string): boolean {
  return pathname === "/m" || pathname.startsWith("/m/");
}

export interface DeviceInfo {
  userAgent: string;
  /** `(pointer: coarse)` — основное устройство ввода палец. */
  coarse: boolean;
  /** Меньшая сторона экрана в CSS-пикселях. */
  shortSide: number;
}

export function readDevice(): DeviceInfo {
  const coarse =
    typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  const s = window.screen;
  return {
    userAgent: navigator.userAgent,
    coarse,
    shortSide: s ? Math.min(s.width, s.height) : Math.min(window.innerWidth, window.innerHeight),
  };
}

/** Телефон, а не планшет и не компьютер.
 *
 *  iPhone называет себя прямо. Android-телефон пишет в UA «Mobile», планшет —
 *  нет (так договорились производители и Chrome). Остальное — сенсорный экран
 *  с меньшей стороной до 600px: так видны и телефоны с чужими браузерами, а
 *  iPad (притворяется Mac'ом) и ноутбуки с сенсорным экраном — нет. */
export function isPhone(d: DeviceInfo = readDevice()): boolean {
  const ua = d.userAgent;
  if (/iPhone|iPod/.test(ua)) return true;
  if (/iPad/.test(ua)) return false;
  if (/Android/.test(ua)) return /Mobile/.test(ua);
  return d.coarse && d.shortSide > 0 && d.shortSide < 600;
}

/** Куда отправить телефон, открывший обычную версию, — или null.
 *
 *  Только то, что в мобильной есть: корень и инбокс (ссылки «Открыть чат» из
 *  уведомлений бота ведут на `/chats?chat=<id>`), кандидаты с карточкой
 *  (`/candidates?candidate=<id>`, старое `/candidates/<id>`) и интервью.
 *  Любая другая страница (карточка вакансии, настройки) остаётся полной
 *  версией. */
export function mobileRedirect(pathname: string, search: string): string | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  const sp = new URLSearchParams(search);
  const next = new URLSearchParams();
  let base: string;
  if (path === "/" || path === "/chats") {
    base = MOBILE_BASE;
    if (sp.get("chat")) next.set("chat", sp.get("chat")!);
  } else if (path === "/candidates" || path === "/interviews") {
    base = MOBILE_BASE + path.slice(1);
  } else {
    const card = /^\/candidates\/([^/]+)$/.exec(path);
    if (!card) return null;
    base = MOBILE_BASE + "candidates";
    next.set("candidate", decodeURIComponent(card[1]));
  }
  // Карточка кандидата открывается над любой страницей — и здесь тоже.
  const candidate = sp.get("candidate");
  if (candidate && !next.has("candidate")) next.set("candidate", candidate);
  const qs = next.toString();
  return qs ? `${base}?${qs}` : base;
}

function desktopChosen(): boolean {
  try {
    return sessionStorage.getItem(DESKTOP_FLAG) === "1";
  } catch {
    return false;
  }
}

/** Вызывается до монтирования: телефон на странице, что есть в мобильной
 *  версии (mobileRedirect), — туда. true — ушли, рисовать ничего не надо. */
export function redirectPhoneToMobile(): boolean {
  const { pathname, search } = window.location;
  if (isMobilePath(pathname) || desktopChosen() || !isPhone()) return false;
  const to = mobileRedirect(pathname, search);
  if (!to) return false;
  window.location.replace(to);
  return true;
}

/** «Полная версия» из меню мобильной: запомнить выбор на сессию и уйти. */
export function openDesktopVersion(path = "/chats") {
  try {
    sessionStorage.setItem(DESKTOP_FLAG, "1");
  } catch {
    /* без хранилища телефон с /chats вернётся в мобильную — не страшно */
  }
  window.location.assign(path);
}
